import React, { useEffect, useMemo } from 'react';
import { WMSLayerInfo } from '@ais-ui/websockets';
import {
  OSMTileLayer,
  BlueTileLayer,
  S57ToTileLayer,
  WMSTileLayer,
  wmsToTileLayer,
} from '@ais-ui/react-openlayers';
import { useSettings } from '../Settings/SettingsProvider';
import { useWMSSettings } from '../WMSLayers/useWMSSettings';
import { getOsmUrlHeadersFromSettings } from '../utils/getOsmUrlHeadersFromSettings';
import { getS57Url } from '../utils/getS57Url';
import { useS57Config } from './S57/useS57Config';

const CARTO_DARK_URL =
  'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png';

export const BaseMap = () => {
  const { settings } = useSettings();
  const wmsSettings = useWMSSettings();
  const { layerName } = useS57Config();

  const { url: osmUrl, headers: osmHeaders } =
    getOsmUrlHeadersFromSettings(settings);

  const activeBaseMapId = settings['ribbonPlugin.settings'].active_base_map;

  // Set basemap-specific shadow color via a data attribute so CSS can
  // reference it without an inline style that would override .dark specificity.
  useEffect(() => {
    document.documentElement.dataset.basemap = activeBaseMapId;
  }, [activeBaseMapId]);

  const activeBaseMap = useMemo(() => {
    if (activeBaseMapId === 's57') {
      return S57ToTileLayer(getS57Url(), layerName);
    }

    if (
      activeBaseMapId === 'blue' ||
      activeBaseMapId === 'osm' ||
      activeBaseMapId === 'carto-dark'
    ) {
      return null;
    }

    for (const wmsInfo of wmsSettings.wmsInfos) {
      const wmsLayerInfo = wmsInfo.layers?.find(
        (layer: WMSLayerInfo) => layer.id === activeBaseMapId,
      );
      if (wmsLayerInfo) {
        const tileLayer = wmsToTileLayer(wmsInfo, wmsLayerInfo);
        return tileLayer;
      }
    }
    return null;
  }, [wmsSettings.wmsInfos, activeBaseMapId, layerName]);

  if (activeBaseMapId === 'blue') {
    return <BlueTileLayer color="#aad3df"></BlueTileLayer>;
  }

  if (activeBaseMapId === 'carto-dark') {
    return <OSMTileLayer key="carto-dark" url={CARTO_DARK_URL} />;
  }

  if (activeBaseMap) {
    return (
      <WMSTileLayer
        key={`${activeBaseMapId}-${layerName}`}
        tileLayer={activeBaseMap}
      ></WMSTileLayer>
    );
  }

  return <OSMTileLayer key="osm" url={osmUrl} headers={osmHeaders} />;
};
